import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CategoryReviewed from './CategoryReviewed';

/**
 * Category Review — choose a design's Category one design at a time.
 *
 * Sibling of OccasionReview, with two deliberate differences:
 *
 *  1. An occasion GATE comes first. ~1,072 designs still carry the vendor's
 *     material name (Temple silver, CZ silver) instead of a real category, which
 *     is too many for one sitting, so the reviewer picks one Primary Occasion and
 *     works that batch through.
 *  2. Category is single-valued, so a number key SELECTS rather than toggles, and
 *     a pick is required before saving — unlike occasions, "none apply" is not a
 *     meaningful answer here.
 *
 * Nothing is pre-selected on purpose. Suggesting the obvious mapping
 * (CZ silver -> CZ) would just get rubber-stamped; the old value is shown as
 * context instead.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

// Mirrors the palette in App.js. Duplicated rather than imported because
// App.js imports this file — importing back would create a cycle.
const C = {
  maroon: '#8B1D3C',
  gold: '#D4AF37',
  bg: '#FFFFFF',
  bgLight: '#F9FAFB',
  textPrimary: '#111827',
  textSecondary: '#6B7280',
  textLight: '#9CA3AF',
  border: '#E5E7EB',
  verified: '#10B981',
};

const PER_PAGE = 50;
// Fetch more once the reviewer is this close to the end of what is loaded.
const TOP_UP_MARGIN = 5;
// Matches category_review.NO_OCCASION — the bucket for designs with no occasion
// decision yet, which would otherwise be unreachable behind every filter.
const NO_OCCASION = '__none__';

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const imageSrc = (url) => {
  if (!url) return '';
  return /^https?:\/\//i.test(url) ? url : `${API_BASE}${url}`;
};

const bucketLabel = (b) =>
  b.label || (b.occasion === NO_OCCASION ? 'No occasion decided yet' : b.occasion);

export default function CategoryReview() {
  // null until an occasion is chosen — the gate is the landing state.
  const [occasion, setOccasion] = useState(null);
  const [buckets, setBuckets] = useState(null);
  const [gateError, setGateError] = useState('');

  const [values, setValues] = useState([]);
  const [queueDefinition, setQueueDefinition] = useState('');
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [picks, setPicks] = useState({});      // designNumber -> string
  const [remaining, setRemaining] = useState(null);
  const [skippedCount, setSkippedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [error, setError] = useState('');
  const [showRecords, setShowRecords] = useState(false);

  // Design numbers saved in this sitting. A Set, so pressing Back and saving the
  // same design again counts once.
  const savedRef = useRef(new Set());
  const [savedCount, setSavedCount] = useState(0);

  // Guards double submission — state updates are async, so two fast Enters can
  // both pass a state-based check before either takes effect.
  const inFlight = useRef(false);
  const fetching = useRef(false);

  const current = queue[index] || null;
  const selected = useMemo(
    () => (current ? picks[current.designNumber] || null : null),
    [current, picks],
  );

  /** Load the gate's options, each with how much work it holds. */
  const fetchBuckets = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/category-review/occasions`,
        { headers: authHeaders() });
      if (!res.ok) throw new Error(`Could not load the filters (${res.status})`);
      const data = await res.json();
      setBuckets(data);
      setValues(data.values || []);
      setGateError('');
    } catch (e) {
      setGateError(e.message || 'Could not load the filters');
      setBuckets({ buckets: [], total: 0 });
    }
  }, []);

  useEffect(() => { fetchBuckets(); }, [fetchBuckets]);

  /**
   * Fetch a batch. `after` is a design number cursor; omit it to start over.
   * Cursor rather than page number means saves (which remove rows from the
   * queue) and skips (which do not) can never shift or hide a batch.
   */
  const fetchBatch = useCallback(async ({ after = null, replace = false } = {}) => {
    if (fetching.current || !occasion) return;
    fetching.current = true;
    try {
      const url = `${API_BASE}/api/category-review/queue?per_page=${PER_PAGE}`
        + `&occasion=${encodeURIComponent(occasion)}`
        + (after ? `&after=${encodeURIComponent(after)}` : '');
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Could not load the queue (${res.status})`);
      const data = await res.json();

      setValues(data.values || []);
      setQueueDefinition(data.queueDefinition || '');
      setRemaining(data.remaining ?? 0);

      const batch = data.designs || [];
      if (replace) {
        setQueue(batch);
        setIndex(0);
        setExhausted(batch.length === 0);
      } else {
        setQueue((prev) => {
          const seen = new Set(prev.map((d) => d.designNumber));
          return [...prev, ...batch.filter((d) => !seen.has(d.designNumber))];
        });
        if (batch.length === 0) setExhausted(true);
      }
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load the queue');
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }, [occasion]);

  useEffect(() => {
    if (!occasion) return;
    setLoading(true);
    fetchBatch({ replace: true });
  }, [occasion, fetchBatch]);

  /** Single-select: picking replaces, picking the same value again clears it. */
  const choose = useCallback((value) => {
    if (!current) return;
    setPicks((prev) => {
      const dn = current.designNumber;
      return { ...prev, [dn]: prev[dn] === value ? null : value };
    });
  }, [current]);

  const advance = useCallback(() => setIndex((prev) => prev + 1), []);

  // Top up from the end of what is loaded, never from the front.
  useEffect(() => {
    if (loading || exhausted || queue.length === 0) return;
    if (index < queue.length - TOP_UP_MARGIN) return;
    fetchBatch({ after: queue[queue.length - 1].designNumber });
  }, [index, queue, loading, exhausted, fetchBatch]);

  const save = useCallback(async () => {
    if (!current || inFlight.current) return;
    // A category is required — there is no "none applies" for this field.
    if (!selected) {
      setError('Pick a category first');
      return;
    }
    inFlight.current = true;
    setSaving(true);
    const dn = current.designNumber;
    try {
      const res = await fetch(
        `${API_BASE}/api/category-review/${encodeURIComponent(dn)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ category: selected }),
        },
      );
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.error || `Save failed (${res.status})`);
      }
      setRemaining(data.remaining ?? remaining);
      savedRef.current.add(dn);
      setSavedCount(savedRef.current.size);
      setError('');
      advance();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }, [current, selected, remaining, advance]);

  // Skip records nothing, so the design stays in the queue for next time.
  const skip = useCallback(() => {
    if (!current) return;
    setSkippedCount((n) => n + 1);
    advance();
  }, [current, advance]);

  const back = useCallback(() => setIndex((prev) => Math.max(0, prev - 1)), []);

  const restart = useCallback(() => {
    savedRef.current = new Set();
    setSavedCount(0);
    setSkippedCount(0);
    setExhausted(false);
    setLoading(true);
    fetchBatch({ replace: true });
  }, [fetchBatch]);

  /** Back to the gate, refreshing the counts so they reflect this session. */
  const changeOccasion = useCallback(() => {
    setOccasion(null);
    setQueue([]);
    setIndex(0);
    setPicks({});
    setExhausted(false);
    setError('');
    fetchBuckets();
  }, [fetchBuckets]);

  // Number keys pick, Enter saves. Arrow keys are deliberately NOT bound.
  useEffect(() => {
    const onKey = (e) => {
      if (showRecords || !occasion) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key >= '1' && e.key <= '9') {
        const value = values[Number(e.key) - 1];
        if (value) { e.preventDefault(); choose(value); }
      } else if (e.key === 'Enter') {
        // Stops a focused Save button from also firing its own click.
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [values, choose, save, showRecords, occasion]);

  if (showRecords) {
    return <CategoryReviewed onBack={() => setShowRecords(false)} />;
  }

  // ---- the gate: pick one occasion before any design is shown
  if (!occasion) {
    if (!buckets) return <div style={S.centre}>Loading…</div>;
    return (
      <div style={S.page}>
        <header style={S.header}>
          <div>
            <h1 style={S.title}>Category Review</h1>
            <p style={S.subtitle}>
              Pick a <strong style={{ color: C.textPrimary }}>Primary Occasion</strong> to
              work through. {buckets.total} design{buckets.total === 1 ? '' : 's'} still
              need a category.
            </p>
          </div>
          <button onClick={() => setShowRecords(true)} style={S.recordsBtn}>
            View records →
          </button>
        </header>

        {gateError && <div style={S.error}>{gateError}</div>}

        <div style={S.gateGrid}>
          {buckets.buckets.map((b) => (
            <button key={b.occasion} style={S.gateCard}
                    disabled={!b.remaining}
                    onClick={() => setOccasion(b.occasion)}>
              <div style={S.gateCount}>{b.remaining}</div>
              <div style={S.gateName}>{bucketLabel(b)}</div>
              <div style={S.gateHint}>
                {b.remaining ? 'needs a category' : 'all done'}
              </div>
            </button>
          ))}
        </div>

        {!buckets.buckets.length && !gateError && (
          <div style={S.centre}>
            <div style={{ fontSize: 44, marginBottom: 12, color: C.verified }}>✓</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary }}>
              Every design has a category
            </div>
          </div>
        )}
      </div>
    );
  }

  if (loading) return <div style={S.centre}>Loading designs…</div>;

  if (!current) {
    return (
      <div style={S.centre}>
        <div style={{ fontSize: 44, marginBottom: 12, color: C.verified }}>✓</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary }}>
          {remaining ? 'Reached the end of this batch' : 'Batch cleared'}
        </div>
        <div style={{ color: C.textSecondary, marginTop: 6 }}>
          {savedCount} design{savedCount === 1 ? '' : 's'} saved this session
          {skippedCount > 0 && `, ${skippedCount} skipped`}.
          {remaining > 0 && ` ${remaining} still to do in this occasion.`}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap',
                      justifyContent: 'center' }}>
          <button style={S.secondaryBtn} onClick={restart}>
            Start this occasion again
          </button>
          <button style={S.secondaryBtn} onClick={changeOccasion}>
            Pick another occasion
          </button>
          <button style={S.secondaryBtn} onClick={() => setShowRecords(true)}>
            View records
          </button>
        </div>
      </div>
    );
  }

  const occasionName = occasion === NO_OCCASION
    ? 'No occasion decided yet' : occasion;

  return (
    <div style={S.page}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}>Category Review</h1>
          <p style={S.subtitle}>
            Set <strong style={{ color: C.textPrimary }}>Category</strong> —
            press <kbd style={S.kbd}>1</kbd>–<kbd style={S.kbd}>{Math.min(values.length, 9)}</kbd> to
            pick, <kbd style={S.kbd}>Enter</kbd> to save
          </p>
        </div>
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-end' }}>
          <button onClick={changeOccasion} style={S.recordsBtn}>
            ← {occasionName}
          </button>
          <button onClick={() => setShowRecords(true)} style={S.recordsBtn}>
            View records →
          </button>
          <div style={S.counter}>
            <div style={S.counterNumber}>{remaining ?? '—'}</div>
            <div style={S.counterLabel}>left in {occasionName}</div>
          </div>
        </div>
      </header>

      {error && <div style={S.error}>{error}</div>}

      <div style={S.card}>
        <div style={S.imageWrap}>
          {current.imageUrl ? (
            <img src={imageSrc(current.imageUrl)} alt={current.designNumber}
                 style={S.image} />
          ) : (
            <div style={S.noImage}>No image</div>
          )}
        </div>

        <div style={S.panel}>
          <div>
            <div style={S.designNumber}>{current.designNumber}</div>
            <div style={S.itemName}>
              {current.itemName || current.productType || 'Design'}
            </div>
            <div style={S.brand}>
              {[current.brand, current.productType,
                current.variantCount > 1 && `${current.variantCount} variants`]
                .filter(Boolean).join(' · ')}
            </div>
          </div>

          {current.currentCategory ? (
            <div style={S.wasBadge}>
              Currently <strong>{current.currentCategory}</strong> — the vendor's
              material name, not a category. Pick the right one below.
            </div>
          ) : (
            <div style={S.blankBadge}>No category recorded yet</div>
          )}

          <div style={S.chips}>
            {values.map((value, i) => {
              const on = selected === value;
              return (
                <button key={value} onClick={() => choose(value)}
                        style={{ ...S.chip, ...(on ? S.chipOn : null) }}
                        aria-pressed={on}>
                  <span style={{ ...S.chipKey, ...(on ? S.chipKeyOn : null) }}>
                    {i + 1}
                  </span>
                  {value}
                </button>
              );
            })}
          </div>

          <div style={S.actions}>
            <button onClick={back} disabled={index === 0}
                    style={{ ...S.secondaryBtn, opacity: index === 0 ? 0.4 : 1 }}>
              ← Back
            </button>
            <button onClick={skip} style={S.secondaryBtn}>Skip →</button>
            <button onClick={save} disabled={saving || !selected}
                    style={{ ...S.saveBtn,
                             opacity: saving || !selected ? 0.5 : 1,
                             cursor: saving || !selected ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save & Next →'}
            </button>
          </div>

          <div style={S.progress}>
            {savedCount} saved this session
            {skippedCount > 0 && ` · ${skippedCount} skipped`}
            {queueDefinition && (
              // Surfaced because the queue is defined by the Category config: if
              // someone edits those values, designs silently enter or leave it.
              <div style={{ marginTop: 6, color: C.textLight }}>
                Queue: {queueDefinition}
              </div>
            )}
            <div style={{ marginTop: 6, color: C.textLight }}>
              Decisions are recorded for review — the catalog's Category field is
              not changed yet.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { padding: '28px 32px', maxWidth: 1180, margin: '0 auto' },
  centre: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '60vh', color: C.textSecondary,
    textAlign: 'center',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    marginBottom: 22, paddingBottom: 18, borderBottom: `1px solid ${C.border}`,
    gap: 24, flexWrap: 'wrap',
  },
  title: {
    fontSize: 30, fontWeight: 800, color: C.textPrimary, margin: 0,
    letterSpacing: '-0.02em',
  },
  subtitle: { fontSize: 14, color: C.textSecondary, margin: '6px 0 0' },
  kbd: {
    background: C.bgLight, border: `1px solid ${C.border}`, borderRadius: 4,
    padding: '1px 6px', fontSize: 12, fontFamily: 'ui-monospace, monospace',
    color: C.textPrimary,
  },
  recordsBtn: {
    padding: '9px 16px', borderRadius: 8, cursor: 'pointer',
    border: `1px solid ${C.border}`, background: C.bg, color: C.textSecondary,
    fontSize: 13.5, fontWeight: 600, marginBottom: 4,
  },
  counter: { textAlign: 'right', lineHeight: 1 },
  counterNumber: {
    fontSize: 38, fontWeight: 800, color: C.maroon, letterSpacing: '-0.03em',
    fontVariantNumeric: 'tabular-nums',
  },
  counterLabel: {
    fontSize: 11, color: C.textLight, textTransform: 'uppercase',
    letterSpacing: '0.09em', marginTop: 5,
  },
  error: {
    background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5',
    borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14,
  },
  gateGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: 16,
  },
  gateCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
    padding: '22px 22px 18px', borderRadius: 14, cursor: 'pointer',
    border: `1px solid ${C.border}`, background: C.bg, textAlign: 'left',
    boxShadow: '0 1px 2px rgba(17,24,39,0.04), 0 10px 26px rgba(17,24,39,0.05)',
  },
  gateCount: {
    fontSize: 34, fontWeight: 800, color: C.maroon, letterSpacing: '-0.03em',
    fontVariantNumeric: 'tabular-nums', lineHeight: 1,
  },
  gateName: { fontSize: 16, fontWeight: 700, color: C.textPrimary, marginTop: 6 },
  gateHint: {
    fontSize: 11, color: C.textLight, textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  card: {
    display: 'grid', gridTemplateColumns: 'minmax(260px, 440px) 1fr', gap: 32,
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 16,
    padding: 24, boxShadow: '0 1px 2px rgba(17,24,39,0.04), 0 12px 32px rgba(17,24,39,0.06)',
    alignItems: 'start',
  },
  imageWrap: {
    background: C.bgLight, borderRadius: 12, overflow: 'hidden',
    aspectRatio: '1 / 1', display: 'flex', alignItems: 'center',
    justifyContent: 'center', border: `1px solid ${C.border}`,
  },
  image: { width: '100%', height: '100%', objectFit: 'contain' },
  noImage: { color: C.textLight, fontSize: 14 },
  panel: { display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 },
  designNumber: {
    fontSize: 26, fontWeight: 800, color: C.textPrimary,
    letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
  },
  itemName: { fontSize: 15, color: C.textSecondary, marginTop: 3 },
  brand: {
    fontSize: 11, color: C.textLight, marginTop: 6,
    textTransform: 'uppercase', letterSpacing: '0.08em',
  },
  wasBadge: {
    background: '#FFFBEB', border: `1px solid ${C.gold}`, color: '#78350F',
    borderRadius: 8, padding: '9px 13px', fontSize: 13.5,
  },
  blankBadge: {
    background: C.bgLight, border: `1px dashed ${C.border}`, color: C.textLight,
    borderRadius: 8, padding: '9px 13px', fontSize: 13.5,
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '11px 16px 11px 11px', borderRadius: 999, cursor: 'pointer',
    border: `1.5px solid ${C.border}`, background: C.bg, color: C.textPrimary,
    fontSize: 15, fontWeight: 600,
    transition: 'transform 120ms ease, border-color 120ms ease, background 120ms ease',
  },
  chipOn: {
    borderColor: C.maroon, background: 'rgba(139,29,60,0.06)', color: C.maroon,
    transform: 'translateY(-1px)',
  },
  chipKey: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 22, height: 22, borderRadius: 6, background: C.bgLight,
    color: C.textLight, fontSize: 11.5, fontWeight: 700,
    fontFamily: 'ui-monospace, monospace',
  },
  chipKeyOn: { background: C.maroon, color: '#fff' },
  actions: {
    display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
    paddingTop: 4,
  },
  secondaryBtn: {
    padding: '11px 18px', borderRadius: 9, cursor: 'pointer',
    border: `1px solid ${C.border}`, background: C.bg, color: C.textSecondary,
    fontSize: 14, fontWeight: 600,
  },
  saveBtn: {
    padding: '11px 26px', borderRadius: 9, border: 'none',
    background: C.maroon, color: '#fff', fontSize: 15, fontWeight: 700,
    marginLeft: 'auto', boxShadow: '0 2px 10px rgba(139,29,60,0.28)',
  },
  progress: {
    fontSize: 12.5, color: C.textLight, borderTop: `1px solid ${C.border}`,
    paddingTop: 14,
  },
};
